import 'select2/dist/js/select2.full';
import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import { PublicEvents, triggerPublicEvent } from '@project/shared/utils/events/js/events';
import * as $ from 'jquery';
import { highlight } from '@project/shared/utils/highlight/js/highlight';
import { floatControlLabel } from '@project/shared/utils/form-control/js/form-control';

@createModule('InputSelect')
/**
 * input-select module implementation.
 *
 * @author Robert Schork <robert.schork@namics.com>
 */
export class InputSelect extends TerrificSpec {
	animationFrameId: number;
	start(resolve: () => void) {
		const $ctx = $(this._ctx);
		const config = {
			search: $ctx.attr('data-search'),
			fullwidth: $ctx.attr('data-fullwidth'),
			autowidth: $ctx.attr('data-autowidth'),
			placeholderWorkaroundList: $ctx.attr('data-placeholder-workaround-list'),
			placeholderWorkaroundTop: $ctx.attr('data-placeholder-workaround-top'),
			customDropdownClass: $ctx.attr('data-custom-dropdown-class'),
			modal: $ctx.attr('data-modal'),
		};

		this._ctx.addEventListener('click', () => floatControlLabel(this._ctx, true));
		this._ctx.addEventListener('focus', () => floatControlLabel(this._ctx, true));
		this._ctx.addEventListener('uxSyncLabel', () => floatControlLabel(this._ctx, true));
		this.animationFrameId = 0;
		$(window).on('resize', () => {
			if (this.animationFrameId > 0) {
				window.cancelAnimationFrame(this.animationFrameId);
			}
			this.animationFrameId = window.requestAnimationFrame(() => {
				this.animationFrameId = 0;
				floatControlLabel(this._ctx);
			});
		});

		// need to timeout in order to prevent select2 rendering timing issue
		setTimeout(() => {
			floatControlLabel(this._ctx);
		}, 500);

		const me = this._ctx;
		const self = this;

		const minSearchResults = config.search ? 0 : -1;
		let width = config.fullwidth ? '100%' : 'element';
		if (config.autowidth) {
			width = 'auto';
		}

		const $select2 = ($ctx as any).select2({
			minimumResultsForSearch: minSearchResults,
			width,
			dropdownAutoWidth: width === 'auto',

			dropdownCssClass: config.customDropdownClass,
			matcher: (params: any, data: any) => {
				if (!params.term) {
					// reset html
					data.html = data.text;
					return data;
				}

				if (data.text.toUpperCase().indexOf(params.term.toUpperCase()) === 0) {
					data.html = highlight(data.text, params.term);
					return data;
				}

				return null;
			},
			escapeMarkup(markup: string) {
				return markup;
			},
			templateResult(item: any) {
				return item.html || item.text;
			},
			templateSelection(item: any) {
				return item.text;
			},
		});

		$ctx.on('select2:open', function() {
			if (config.modal) {
				const $body = $('body');
				const $html = $('html');
				if ($body.outerHeight() > $html.outerHeight()) {
					$html.addClass('no-scrolling--with-scrollbar');
				} else {
					$html.addClass('no-scrolling--without-scrollbar');
				}
				$body.addClass('state-select2-dropdown--product-finder-teaser');

				setTimeout(() => {
					$body.addClass('state-select2-dropdown--product-finder-teaser-activated');
				}, 300);
			}

			floatControlLabel(me, true);

			setTimeout(() => {
				const $optionsElement = $('.select2-results__options');
				const hasScrollbar = $optionsElement.get(0).scrollHeight > $optionsElement.height();

				if (hasScrollbar) {
					$('.select2-dropdown').addClass('select2-dropdown--add-space select2-dropdown--with-icon');
				} else if (!hasScrollbar && $('.select2-dropdown').hasClass('select2-dropdown--with-icon')) {
					$('.select2-dropdown').removeClass('select2-dropdown--with-icon');
				}

				// add listeneer to control the scroll icon
				$optionsElement.on('scroll', function() {
					// bottom end of scroll container reached
					if ($(this).scrollTop() + $(this).innerHeight() >= $(this)[0].scrollHeight) {
						$('.select2-dropdown').removeClass('select2-dropdown--with-icon');
					} else {
						// scroll is somewhere else
						$('.select2-dropdown').addClass('select2-dropdown--with-icon');
					}
				});

				if (config.placeholderWorkaroundList) {
					$optionsElement.prepend(
						`<li class="select2-results__option select2-close-handler">
									${config.placeholderWorkaroundList}
									<span class="select2-close-handler-icon js-trigger-dropdown-close"></span>
								</li>`
					);
					$('.js-trigger-dropdown-close').on('click', () => $select2.select2('close'));
				}

				/**
				 * Overlay can have a closing text before the searchbox. As everything but the result list is cached, the
				 * trigger inserted outside the result list needs to be deleted manually
				 */
				if (config.placeholderWorkaroundTop) {
					$('.js-trigger-dropdown').remove();
					$('.select2-dropdown').prepend(
						`<div class="select2-close-handler js-trigger-dropdown">
								${config.placeholderWorkaroundTop}
								<span class="select2-close-handler-icon js-trigger-dropdown-close"></span>
							</div>`
					);
					$('.js-trigger-dropdown-close').on('click', () => $select2.select2('close'));
				}
			}, 5);

			triggerPublicEvent(PublicEvents.uxShow, self, me);
		});

		$ctx.on('select2:close', () => {
			floatControlLabel(me, false);
			triggerPublicEvent(PublicEvents.uxHide, self, me);
			$('body')
				.removeClass('state-select2-dropdown--product-finder-teaser')
				.removeClass('state-select2-dropdown--product-finder-teaser-activated');
			$('html')
				.removeClass('no-scrolling--with-scrollbar')
				.removeClass('no-scrolling--without-scrollbar');
		});

		$ctx.on('select2:select', () => {
			floatControlLabel(me, false);
			triggerPublicEvent(PublicEvents.uxChange, self, me, {});
		});

		if (config.modal) {
			$ctx.on('select2:opening', this._openModal.bind(this));
			$ctx.on('select2:close', this._closeModal.bind(this));
		}

		resolve();
	}
	stop() {}

	/**
	 * Set the value of the input field
	 */
	setValue(value: string) {
		(this._ctx as HTMLSelectElement).value = value;
	}

	/**
	 * Get the value of the input field
	 */
	getValue() {
		return $(this._ctx as HTMLSelectElement)
			.find(':selected')
			.get(0).innerText;
	}

	getOptionValue() {
		return (this._ctx as HTMLSelectElement).value;
	}

	/**
	 * send event in order to open modal window
	 * add class in order to set z-index
	 *
	 * @param ev
	 */
	_openModal(ev: any) {
		$(window).trigger('modalWindow:open');
		$(ev.currentTarget)
			.next('.select2.select2-container')
			.addClass('state-m-market-switch--overlay');
	}

	_closeModal(ev: any) {
		$(window).trigger('modalWindow:close');
		$(ev.currentTarget)
			.next('.select2.select2-container')
			.removeClass('state-m-market-switch--overlay');
	}
}
